import Button from "components/button";
import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { Link } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/solid";
import { useOne, useTable, useTranslation, useUpdate } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { _openUrlOnNewTab, isHaveActionPermission } from "utills/helpers";
import {
  PromotionCouponCard,
  PromotionCouponFilter,
  PromotionCouponTable,
} from "components/shared/promotion/coupon";
import { ConfirmModal } from "components/confirmModal";
import { useState } from "react";
import {
  CouponInfoModal,
  RedirectCreatePromotionModal,
} from "components/shared/promotion/modal";
import { IEventIdsItem, IPromotionCouponItem, UserType } from "interfaces";
import { endpointService } from "components/shared/services/endpointService";
import { DownloadIcon } from "@heroicons/react/outline";
import { ExportButton } from "components/exportButton";

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  status: "",
  couponCode: "",
  submit: () => {},
};

const INITIAL_COUPON_INFO_MODAL_CONFIG: any = {
  open: false,
  data: null,
};

export const WPSPromotionCouponList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();
  const { translate } = useTranslation();
  const [confirmModalConfig, setConfirmModalConfig] = useState(
    INITIAL_ACTION_MODAL_CONFIG
  );

  const [couponInfoModalConfig, setCouponInfoModalConfig] = useState(
    INITIAL_COUPON_INFO_MODAL_CONFIG
  );

  const [showCreatePromotionModal, setShowCreatePromotionModal] =
    useState(false);

  const { tableQuery, filters, setFilters } = useTable<IPromotionCouponItem>({
    resource: "promotion",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
    },
  });

  const { data: eventsData, isLoading: isLoadingEvents } = useOne<
    IEventIdsItem[]
  >({
    resource: endpointService.getPromotionEndPoint("getPromotionEventDropdown"),
    id: "",
    meta: {
      resource: "data",
    },
  });

  const _toggleActive = (id: string, isActive: boolean) => {
    updateMutation(
      {
        resource: "promotion/coupons",
        id,
        meta: {
          URLSuffix: isActive ? "status/true" : "status/false",
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: () => {
          tableQuery.refetch();
          toast.success("Update successfully");
        },
      }
    );
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap((item) =>
      "field" in item ? item : []
    );
    const pageIndex = updatedFilters.findIndex(
      (filter) => filter.field === "page"
    );
    if (pageIndex < 0) {
      updatedFilters.push({ field: "page", operator: "eq", value: page });
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  };

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      status: confirmModalConfig.status,
      couponCode: confirmModalConfig.couponCode,
    });
  };

  const _onCloseCouponInfoModal = () => {
    setCouponInfoModalConfig({
      ...INITIAL_COUPON_INFO_MODAL_CONFIG,
      data: couponInfoModalConfig.data,
    });
  };

  if (tableQuery.isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="wrapper p-4">
      <Breadcrumbs
        additionalLabel={
          <div className="max-w-full flex items-center gap-x-3">
            {isHaveActionPermission("coupon", "promotion", "export") ? (
              <ExportButton
                type={UserType.WPS}
                filters={filters}
                title={translate("download-coupon-list")}
                resource="promotion/coupons/export"
                className="w-11 h-11 bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85 sm:w-auto"
              />
            ) : (
              <></>
            )}
            {isHaveActionPermission("coupon", "promotion", "create") ? (
              <Button
                className="w-11 h-11 sm:w-auto"
                onClick={() => setShowCreatePromotionModal(true)}
              >
                <span className="sr-only">Create Promotion</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  {translate("create-promotion")}
                </span>
              </Button>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container">
        <PromotionCouponFilter
          filters={filters}
          setFilters={setFilters}
          events={eventsData?.data ?? []}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length && !tableQuery.isFetching && (
          <div className="text-center">No data</div>
        )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <PromotionCouponCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={(id, isActive, couponCode) =>
                  setConfirmModalConfig({
                    open: true,
                    status: isActive ? "activate" : "deactivate",
                    couponCode: couponCode,
                    submit: () => _toggleActive(id, isActive),
                  })
                }
                _showInfoModal={(data: IPromotionCouponItem) =>
                  setCouponInfoModalConfig({
                    open: true,
                    data: data,
                  })
                }
              />
            </div>
            <div className="hidden sm:block">
              <PromotionCouponTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={(id, isActive, couponCode) =>
                  setConfirmModalConfig({
                    open: true,
                    status: isActive ? "activate" : "deactivate",
                    couponCode: couponCode,
                    submit: () => _toggleActive(id, isActive),
                  })
                }
                _showInfoModal={(data: IPromotionCouponItem) =>
                  setCouponInfoModalConfig({
                    open: true,
                    data: data,
                  })
                }
              />
            </div>
          </>
        ) : (
          <></>
        )}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_item ?? 0,
                total: tableQuery?.data?.paging.total,
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {tableQuery.isLoading ||
      (tableQuery.isFetching && tableQuery.isPreviousData) ||
      updateMutationIsLoading ? (
        <FullScreenLoading />
      ) : (
        <></>
      )}
      <ConfirmModal
        key="confirm-modal"
        title=""
        content=""
        customContent={
          <div className="mb-4 text-center text-lg font-medium">
            {translate("modal.toggleCouponStatus", {
              status: translate(confirmModalConfig.status),
              couponCode: confirmModalConfig.couponCode,
            })}
          </div>
        }
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
      />
      <CouponInfoModal
        onClose={() => _onCloseCouponInfoModal()}
        open={couponInfoModalConfig.open}
        couponData={couponInfoModalConfig.data}
        events={eventsData?.data ?? []}
      />
      <RedirectCreatePromotionModal
        open={showCreatePromotionModal}
        onClose={() => setShowCreatePromotionModal(false)}
      />
    </div>
  );
};
